import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseFileResponse, parseListResponse, parseResponse } from "lib/fetch";
import {
    cancelBookingRequest,
    downloadBookingSalesSuccessionRequest,
    getBookingRequest,
    getBookingsRequest,
    getLastBookingsRequest,
} from "lib/api/booking";
import type { BookingInterface } from "types/booking";

export { BookingStatus } from "types/booking";
export type { BookingData, BookingInterface, BookingUserInterface } from "types/booking";

export const bookingKeys = {
    all: ["booking"] as const,
    lists: () => [...bookingKeys.all, "list"] as const,
    details: () => [...bookingKeys.all, "detail"] as const,
    detail: (id: string) => [...bookingKeys.details(), id] as const,
};

export const fetchBookings = (params?: HashMap<unknown>) =>
    parseListResponse<BookingInterface>(getBookingsRequest(params));

export const fetchBooking = (id: string, params?: HashMap<unknown>) =>
    parseResponse<BookingInterface>(getBookingRequest(id, params));

export const fetchLastBookings = () => parseResponse<unknown>(getLastBookingsRequest());

export const cancelBooking = (id: string) => parseResponse<BookingInterface>(cancelBookingRequest(id));

export const downloadBookingSalesSuccession = ({ booking_id }: { booking_id: string }) =>
    parseFileResponse(downloadBookingSalesSuccessionRequest(booking_id));

export const bookingDetailQueryOptions = (id: string | undefined) =>
    queryOptions<BookingInterface>({
        queryKey: id ? bookingKeys.detail(id) : bookingKeys.detail("__missing__"),
        queryFn: () => {
            if (!id) return Promise.reject(new Error("Missing booking id"));
            return fetchBooking(id);
        },
        enabled: Boolean(id),
    });

export const useBooking = (id: string | undefined) => useQuery(bookingDetailQueryOptions(id));

export const useCancelBooking = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => cancelBooking(id),
        onSuccess: (_response, id) => {
            queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
            queryClient.invalidateQueries({ queryKey: bookingKeys.detail(id) });
        },
    });
};

const bookingDataFetcher = {
    all: fetchBookings,
    lasts: fetchLastBookings,
    one: fetchBooking,
    cancel: cancelBooking,
    downloadSalesSuccession: downloadBookingSalesSuccession,
};

export default bookingDataFetcher;
